import { useEffect, useState, useCallback, useRef } from "react";
import { authenticatedFetch } from '@/services/auth/authenticatedFetch.js';

const useProspectPicture = (prospectId) => {
    const [profilePicture, setProfilePicture] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestVersion = useRef(0);
    const currentProspectId = useRef(prospectId);
    currentProspectId.current = prospectId;

    const loadPicture = useCallback(async (id) => {
        const response = await authenticatedFetch(
            `${window.ENV.BACKEND_URL}/api/secured/admin/prospect/picture/${id}`,
            { method: 'GET' }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return URL.createObjectURL(await response.blob());
    }, []);

    const getProspectPicture = useCallback(async () => {
        if (!prospectId) return;
        const version = ++requestVersion.current;
        setLoading(true);
        setError(null);
        try {
            const imageUrl = await loadPicture(prospectId);
            if (version === requestVersion.current && prospectId === currentProspectId.current) {
                setProfilePicture(imageUrl);
            } else {
                URL.revokeObjectURL(imageUrl);
            }
        } catch (err) {
            if (version === requestVersion.current) setError(err);
        } finally {
            if (version === requestVersion.current) setLoading(false);
        }
    }, [loadPicture, prospectId]);

    const uploadProspectPicture = async (file) => {
        if (!file || !prospectId) return;
        const version = ++requestVersion.current;
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append("picture", file);

            const response = await authenticatedFetch(
                `${window.ENV.BACKEND_URL}/api/secured/admin/prospect/picture/${prospectId}`,
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const imageUrl = await loadPicture(prospectId);
            if (version === requestVersion.current && prospectId === currentProspectId.current) {
                setProfilePicture(imageUrl);
            } else {
                URL.revokeObjectURL(imageUrl);
            }
            return { data: imageUrl, error: null };
        } catch (err) {
            if (version === requestVersion.current) setError(err);
            return { data: null, error: err };
        } finally {
            if (version === requestVersion.current) setLoading(false);
        }
    };

    useEffect(() => {
        setProfilePicture(null);
        setError(null);
        void getProspectPicture();
        return () => { requestVersion.current += 1; };
    }, [getProspectPicture]);

    useEffect(() => {
        return () => { if (profilePicture) URL.revokeObjectURL(profilePicture); };
    }, [profilePicture]);

    return {
        data: profilePicture,
        refetch: getProspectPicture,
        profilePicture,
        loading,
        error,
        getProspectPicture,
        uploadProspectPicture,
    };
};

export default useProspectPicture;
